/**
 * GoalServing — the goal-scoped "Serving" section.
 *
 * A self-contained, live view of the goal champion's deployment and its
 * inference activity: status + KPI tiles, a deployment details card
 * (type, endpoint, identifiers, timing) and a compact recent-inferences
 * list with sparklines. Deep-links to the full workspace surfaces
 * (deployment detail / inference detail) for the heavy actions
 * (run inference, API guide, delete) rather than duplicating them.
 *
 * Data comes from two existing slices:
 *   * deployment_detail  — GET /deployments/{promotion_id}
 *   * inferences_list    — GET /inference?deployment_id=…
 * The goal's champion promotion id arrives on activity.resources.
 */
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDistanceToNow, format } from '../../utils/datetime';
import {
  Box, Divider, Grid, Stack, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import { InferencesIcon, DeploymentsIcon } from '../global/CustomIcons';
import SpeedIcon from '@mui/icons-material/Speed';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LaunchIcon from '@mui/icons-material/Launch';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CodeIcon from '@mui/icons-material/Code';
import HistoryIcon from '@mui/icons-material/History';

import useRealtimeRefresh from '../../realtime/useRealtimeRefresh';
import { fetchRequest as getDeploymentDetail, clearState as clearDeployment } from '../../redux/actions/deployments/detail';
import { fetchRequest as fetchInferences } from '../../redux/actions/inferences/list';
import { fetchRequest as fetchActivity } from '../../redux/actions/discovery/goalActivity';
import { Surface, KPICard, StatusDot, formatNumber } from '../dashboard/components/DashboardPrimitives';
import InferenceMiniChart, { predictionFinal } from '../inferences/InferenceMiniChart';
import { CMButtonOutlinedSmall } from '../../containers/styledComponents';
import { useDisplayNames } from '../../utils/displayNames';
import { useSignalUnits } from '../../utils/units';

const TERMINAL_STATES = new Set(['failed', 'inactive', 'archived']);
const INFLIGHT_STATES = new Set(['deploying', 'initializing', 'starting']);

// Compact SI suffixes (23k) concatenated with "ms" read as "23kms". Speak
// latency in the unit a human expects: milliseconds under a second, else seconds.
const formatLatencyMs = (ms) => {
  const v = Number(ms);
  if (!Number.isFinite(v) || v <= 0) return '—';
  if (v < 1000) return `${Math.round(v)}ms`;
  return `${(v / 1000).toFixed(v >= 10_000 ? 0 : 1)}s`;
};

// Mirror resolve_effective_status precedence (BE): terminal Mongo states
// win, then live readiness, then in-flight, then a sensible fallback. Keeps
// the dot colour and label in lock-step (no "green dot + Failed text").
const resolveServingStatus = (d) => {
  if (!d) return { label: 'Not deployed', tone: 'neutral', serving: false, inflight: false };
  const dbStatus = String(d.status || '').toLowerCase();
  if (TERMINAL_STATES.has(dbStatus)) {
    return {
      label: dbStatus === 'failed' ? 'Failed' : dbStatus.charAt(0).toUpperCase() + dbStatus.slice(1),
      tone: dbStatus === 'failed' ? 'error' : 'neutral',
      serving: false,
      inflight: false,
    };
  }
  if (d.ready) return { label: 'Serving', tone: 'success', serving: true, inflight: false };
  const live = String(d.live_status || '').toLowerCase();
  if (INFLIGHT_STATES.has(live) || INFLIGHT_STATES.has(dbStatus)) {
    return { label: 'Deploying', tone: 'warning', serving: false, inflight: true };
  }
  if (live === 'failed') return { label: 'Failed', tone: 'error', serving: false, inflight: false };
  return {
    label: dbStatus ? dbStatus.charAt(0).toUpperCase() + dbStatus.slice(1) : 'Unknown',
    tone: 'neutral',
    serving: false,
    inflight: false,
  };
};

const DEPLOYMENT_TYPE_LABELS = {
  shared: 'Shared',
  modelmesh_shared: 'Shared',
  dedicated: 'Dedicated',
  kserve_dedicated: 'Dedicated',
  foundation: 'Foundation · shared',
  external_fm: 'External FM · shared',
};
const fmtDeploymentType = (t) => DEPLOYMENT_TYPE_LABELS[t] || t || '—';

const safeRelative = (d) => {
  try { return d ? `${formatDistanceToNow(new Date(d))} ago` : null; }
  catch (_e) { return null; }
};
const safeDateTime = (d) => {
  try { return d ? format(new Date(d), 'MMM d, yyyy HH:mm') : '—'; }
  catch (_e) { return '—'; }
};
const safeShortDateTime = (d) => {
  try { return d ? format(new Date(d), 'MMM d, HH:mm') : null; }
  catch (_e) { return null; }
};
// Humanize a serving cadence (seconds) into "every hour" / "every 15 min".
const fmtCadence = (sec) => {
  if (!sec || sec <= 0) return null;
  if (sec % 86400 === 0) { const d = sec / 86400; return `every ${d === 1 ? 'day' : `${d} days`}`; }
  if (sec % 3600 === 0) { const h = sec / 3600; return `every ${h === 1 ? 'hour' : `${h} hours`}`; }
  if (sec % 60 === 0) { const m = sec / 60; return `every ${m === 1 ? 'minute' : `${m} min`}`; }
  return `every ${sec}s`;
};

// Relative label for the next scheduled run; collapses past/imminent to "Due now".
const nextRunLabel = (ts) => {
  if (!ts) return null;
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return null;
    if (d.getTime() - Date.now() <= 1000) return 'Due now';
    return `in ${formatDistanceToNow(d)}`;
  } catch (_e) { return null; }
};

const fmtFinal = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return v.toFixed(abs >= 1 ? 1 : 3);
};

const severityColor = (severity, theme) => ({
  success: theme.palette.success.main,
  primary: theme.palette.primary.main,
  warning: theme.palette.warning.main,
  error: theme.palette.error.main,
  neutral: theme.palette.text.primary,
}[severity] || theme.palette.text.primary);

const SectionHeader = ({ icon, label, tone, action }) => {
  const theme = useTheme();
  const accent = tone === 'success' ? theme.palette.success.main : theme.palette.text.secondary;
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{
          width: 28, height: 28, borderRadius: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(accent, 0.12), color: accent,
        }}>
          {icon}
        </Box>
        <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary' }}>
          {label}
        </Typography>
      </Stack>
      {action}
    </Stack>
  );
};

const DetailRow = ({ label, children }) => (
  <Stack direction="row" spacing={2} alignItems="baseline" justifyContent="space-between" sx={{ py: 0.75 }}>
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ minWidth: 0, textAlign: 'right' }}>{children}</Box>
  </Stack>
);

const Mono = ({ children }) => (
  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' }}>
    {children}
  </Typography>
);

/* One recent inference row — timestamp, final value, latency, sparkline. */
const InferenceRow = ({ inf, historyBySegment, onOpen }) => {
  const theme = useTheme();
  const unitOf = useSignalUnits();
  const ok = inf.response?.success !== false && !inf.response?.error;
  const latency = Math.round(inf.performance?.latency_ms ?? 0);
  const pred = (inf.response?.predictions || [])[0];
  const final = predictionFinal(pred);
  const segId = (inf.segment_metadata || {}).segment_id;
  const segHistory = historyBySegment?.[segId];
  const labelsUsed = (inf.segment_metadata || {}).labels_used || [];
  const histLabel = pred?.label || labelsUsed[0];
  const histSeries = segHistory?.labels?.[histLabel];
  const historyPts = histSeries
    ? histSeries.timestamps
      .map((t, i) => ({ x: t, y: histSeries.values[i] }))
      .filter((p) => p.y !== null && p.y !== undefined)
    : [];
  const intervalSec = segHistory?.interval_seconds || 60;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      onClick={onOpen}
      sx={{
        py: 1, px: 0.5, borderRadius: 1, cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <StatusDot tone={ok ? 'success' : 'error'} size={7} />
      <Box sx={{ minWidth: 92, flexShrink: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
          {safeRelative(inf.timestamp) || '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {ok ? `${latency}ms` : 'Failed'}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontSize: 13 }} noWrap>
          {inf.segment_name || segId || '—'}
        </Typography>
      </Box>
      <Box sx={{ width: 110, flexShrink: 0 }}>
        <InferenceMiniChart prediction={pred} history={historyPts} intervalSec={intervalSec} height={36} unit={unitOf(histLabel)} />
      </Box>
      <Box sx={{ width: 72, textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="body2" sx={{
          fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums',
          color: severityColor(final.severity, theme),
        }}>
          {fmtFinal(final.value)}
        </Typography>
        {final.bandPct !== null && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            ±{final.bandPct.toFixed(1)}%
          </Typography>
        )}
      </Box>
      <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
    </Stack>
  );
};

const GoalServing = ({ goalId, goal, activity: activityProp, navigate }) => {
  const theme = useTheme();
  const nameOf = useDisplayNames();
  const dispatch = useDispatch();

  // The activity prop is owned by GoalActivity (Overview tab), which clears it
  // on unmount — so on the Serving tab it's usually empty. Fetch it here and
  // prefer the live store value, falling back to whatever the parent passed.
  const activityStore = useSelector((s) => s.discovery_goal_activity?.response);
  const activity = activityStore || activityProp;

  useEffect(() => {
    if (goalId) dispatch(fetchActivity({ goalId }));
  }, [goalId, dispatch]);

  const resources = activity?.resources || {};
  const stats = activity?.stats || {};
  const deploy = stats.deploy || {};
  const serving = stats.serving || {};
  const deploymentId = resources.deployment_id;
  // Goal data mode: live goals re-forecast on a cadence; historical goals
  // (fixed window) serve once on demand and are never rescheduled.
  const goalIsLive = (activity?.config?.live) !== false;

  const deployment = useSelector((s) => s.deployment_detail?.data);
  const deploymentLoading = useSelector((s) => s.deployment_detail?.loading);
  const inferencesState = useSelector((s) => s.inferences_list);
  const historyBySegment = inferencesState?.response?.history_by_segment || {};
  // The inferences_list slice is shared with the workspace Inferences page —
  // filter to this deployment so we never flash another deployment's rows.
  const inferences = useMemo(() => {
    const all = inferencesState?.response?.inferences || [];
    if (!deploymentId) return [];
    return all.filter((i) => i.deployment_id === deploymentId);
  }, [inferencesState, deploymentId]);

  // Pull the champion's live deployment + its recent inferences whenever the
  // goal exposes a deployment id. Clear on unmount so a different goal's
  // Serving tab doesn't flash this one's data.
  useEffect(() => {
    if (!deploymentId) return undefined;
    dispatch(getDeploymentDetail({ promotion_id: deploymentId }));
    dispatch(fetchInferences({ deployment_id: deploymentId, page: 1, limit: 8 }));
    return () => { dispatch(clearDeployment()); };
  }, [deploymentId, dispatch]);

  // Live (no polling): refetch on any deployment/inference transition that
  // lands on the goal/workspace channels.
  useRealtimeRefresh(
    (msg) => typeof msg.event === 'string'
      && (msg.event.indexOf('deployment.') === 0 || msg.event.indexOf('inference.') === 0),
    () => {
      if (goalId) dispatch(fetchActivity({ goalId }));
      if (!deploymentId) return;
      dispatch(getDeploymentDetail({ promotion_id: deploymentId }));
      dispatch(fetchInferences({ deployment_id: deploymentId, page: 1, limit: 8 }));
    },
  );

  const status = useMemo(() => resolveServingStatus(deployment), [deployment]);

  const inferenceCount = deployment?.inference_count ?? serving.inference_count ?? 0;
  const lastInferenceAt = serving.last_inference_at
    || deployment?.last_used_at
    || inferences[0]?.timestamp;

  const avgLatency = useMemo(() => {
    const ok = inferences.filter((i) => i.response?.success !== false && !i.response?.error);
    // A latency of 0 is never real — older scheduled forecasts logged 0 as a
    // placeholder, so treat non-positive values as "unmeasured" and skip them.
    const vals = ok
      .map((i) => i.performance?.latency_ms)
      .filter((v) => typeof v === 'number' && v > 0);
    if (!vals.length) return null;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }, [inferences]);

  const nextInferenceAt = serving.next_inference_at;
  const cadenceLabel = fmtCadence(serving.interval_seconds);
  const nextRun = useMemo(() => nextRunLabel(nextInferenceAt), [nextInferenceAt]);

  const openApiGuide = () => navigate(`/deployment/${deploymentId}?tab=api`);

  // ── Not deployed yet ────────────────────────────────────────────────
  if (!deploymentId) {
    const deploySub = deploy.live ? 'Live and serving' : (deploy.status || 'not deployed');
    return (
      <Surface sx={{ p: 4, textAlign: 'center' }}>
        <Stack spacing={1.5} alignItems="center">
          <Box sx={{
            width: 48, height: 48, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.text.secondary, 0.1), color: 'text.secondary',
          }}>
            <SensorsIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Not serving yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
            Once you deploy this goal&apos;s best model it serves live forecasts here —
            with status, throughput and a feed of recent inferences. Current state:{' '}
            <strong>{deploySub}</strong>.
          </Typography>
          <CMButtonOutlinedSmall
            startIcon={<NorthEastIcon />}
            onClick={() => navigate(`/goals/${goalId}/models`)}
            sx={{ mt: 1 }}
          >
            Review &amp; deploy best model
          </CMButtonOutlinedSmall>
        </Stack>
      </Surface>
    );
  }

  return (
    // useFlexGap: without it Stack resets child margins, which breaks the
    // negative-margin alignment of the direct <Grid container> child below.
    <Stack spacing={2} useFlexGap>
      {/* ── Status header ─────────────────────────────────────────────── */}
      <Surface sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(status.serving ? theme.palette.success.main : theme.palette.text.secondary, 0.12),
              color: status.serving ? 'success.main' : 'text.secondary',
            }}>
              <DeploymentsIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <StatusDot tone={status.tone} size={8} />
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                  {status.label}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {fmtDeploymentType(deployment?.deployment_type)}
                {goal?.target_key ? <> · serving <strong>{nameOf(goal.target_key)}</strong></> : null}
                {typeof goal?.horizon_bins === 'number' ? <> · {goal.horizon_bins} steps ahead</> : null}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap" useFlexGap>
            <CMButtonOutlinedSmall
              startIcon={<LaunchIcon />}
              onClick={() => navigate(`/deployment/${deploymentId}`)}
            >
              Run inference
            </CMButtonOutlinedSmall>
            <CMButtonOutlinedSmall
              startIcon={<CodeIcon />}
              onClick={openApiGuide}
            >
              API guide
            </CMButtonOutlinedSmall>
            <CMButtonOutlinedSmall
              startIcon={<DeploymentsIcon />}
              onClick={() => navigate(`/deployment/${deploymentId}`)}
            >
              Open deployment
            </CMButtonOutlinedSmall>
          </Stack>
        </Stack>
      </Surface>

      {/* ── KPI tiles ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        <KPICard
          tone={status.tone}
          icon={<SensorsIcon sx={{ fontSize: 18 }} />}
          label="Status"
          primary={<Box sx={{ fontSize: 20, lineHeight: 1.25 }}>{status.label}</Box>}
          subtitle={status.serving ? 'Healthy' : (status.inflight ? 'Coming up…' : 'Not serving')}
        />
        <KPICard
          tone={inferenceCount > 0 ? 'success' : 'neutral'}
          icon={<InferencesIcon sx={{ fontSize: 18 }} />}
          label="Inferences"
          primary={formatNumber(inferenceCount)}
          subtitle={inferenceCount > 0 ? 'total served' : 'none yet'}
        />
        <KPICard
          tone="neutral"
          icon={<ScheduleIcon sx={{ fontSize: 18 }} />}
          label="Last inference"
          primary={<Box sx={{ fontSize: 18, lineHeight: 1.3 }}>{safeRelative(lastInferenceAt) || '—'}</Box>}
          subtitle={
            status.serving && nextInferenceAt
              ? `Next: ${nextRun === 'Due now' ? 'due now' : (safeShortDateTime(nextInferenceAt) || '—')}`
              : (lastInferenceAt ? 'most recent run' : (status.serving ? 'awaiting first run' : '—'))
          }
        />
        <KPICard
          tone="neutral"
          icon={<SpeedIcon sx={{ fontSize: 18 }} />}
          label="Avg latency"
          primary={avgLatency != null ? formatLatencyMs(avgLatency) : '—'}
          subtitle={avgLatency != null ? 'recent inferences' : 'no samples'}
        />
      </Box>

      <Grid container spacing={2}>
        {/* ── Deployment details ──────────────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Surface sx={{ p: 2.5, height: '100%' }}>
            <SectionHeader icon={<DeploymentsIcon sx={{ fontSize: 18 }} />} label="Deployment" />
            <Stack divider={<Divider />}>
              <DetailRow label="Type">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fmtDeploymentType(deployment?.deployment_type)}
                </Typography>
              </DetailRow>
              {deployment?.namespace && (
                <DetailRow label="Namespace"><Mono>{deployment.namespace}</Mono></DetailRow>
              )}
              <DetailRow label="Deployment ID">
                <Tooltip title={deploymentId} arrow placement="top-start"><Mono>{deploymentId}</Mono></Tooltip>
              </DetailRow>
              {deployment?.training_id && (
                <DetailRow label="Training ID">
                  <Tooltip title={deployment.training_id} arrow placement="top-start">
                    <Mono>{deployment.training_id}</Mono>
                  </Tooltip>
                </DetailRow>
              )}
              <DetailRow label="Promoted">
                <Typography variant="body2">{safeDateTime(deployment?.promoted_at)}</Typography>
              </DetailRow>
              {deployment?.deployed_at && (
                <DetailRow label="Deployed">
                  <Typography variant="body2">{safeDateTime(deployment.deployed_at)}</Typography>
                </DetailRow>
              )}
              {cadenceLabel && (
                <DetailRow label="Cadence">
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{cadenceLabel}</Typography>
                </DetailRow>
              )}
            </Stack>

            {status.serving && goalIsLive && nextRun && (
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{
                  mt: 2, p: 1.25, borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    Next inference
                  </Typography>
                  <Tooltip title={safeDateTime(nextInferenceAt)} arrow placement="top-start">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {nextRun === 'Due now' ? 'Due now' : nextRun}
                      {cadenceLabel ? <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · {cadenceLabel}</Box> : null}
                    </Typography>
                  </Tooltip>
                </Box>
              </Stack>
            )}

            {status.serving && !goalIsLive && (
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{
                  mt: 2, p: 1.25, borderRadius: 1,
                  bgcolor: alpha(theme.palette.text.secondary, 0.06),
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0, mt: 0.25 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, fontWeight: 700 }}>
                    Historical data — no scheduled re-runs
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    This goal runs on a fixed window, so the forecast isn&apos;t
                    re-computed on a cadence. Trigger one anytime with{' '}
                    <strong>Run inference</strong>, or switch to live data in
                    Settings to forecast continuously.
                  </Typography>
                </Box>
              </Stack>
            )}
          </Surface>
        </Grid>

        {/* ── Recent inferences ───────────────────────────────────────── */}
        <Grid item xs={12} md={7}>
          <Surface sx={{ p: 2.5, height: '100%' }}>
            <SectionHeader
              icon={<InferencesIcon sx={{ fontSize: 18 }} />}
              label="Recent inferences"
              action={(
                <Typography
                  variant="caption"
                  onClick={() => navigate('/inference')}
                  sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                >
                  View all
                </Typography>
              )}
            />
            {inferences.length > 0 ? (
              <Stack divider={<Divider />}>
                {inferences.map((inf) => (
                  <InferenceRow
                    key={inf.uid}
                    inf={inf}
                    historyBySegment={historyBySegment}
                    onOpen={() => navigate(`/inferences/${inf.uid}`)}
                  />
                ))}
              </Stack>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {deploymentLoading ? 'Loading…' : 'No inferences yet'}
                </Typography>
                {!deploymentLoading && (
                  <Typography variant="caption" color="text.secondary">
                    {status.serving
                      ? 'Run an inference or wait for the next scheduled forecast.'
                      : 'Inferences appear here once the deployment is serving.'}
                  </Typography>
                )}
              </Box>
            )}
          </Surface>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default GoalServing;
